"use client";

import { useEffect } from "react";
import NextTopLoader from "nextjs-toploader";

/**
 * CustomTopLoader Component
 * 
 * Enhanced wrapper around NextTopLoader that emits custom events
 * for better integration with animated components.
 * 
 * Features:
 * - Custom event emission for loading states
 * - Seamless integration with existing NextTopLoader
 * - TypeScript support
 * - Performance optimized
 */
const CustomTopLoader: React.FC = () => {
  useEffect(() => {
    // Override the original NextTopLoader methods to emit custom events
    const originalStart = (window as any).NProgress?.start;
    const originalDone = (window as any).NProgress?.done;

    if (originalStart) {
      (window as any).NProgress.start = function() {
        originalStart.call(this);
        // Emit custom event for loading start
        window.dispatchEvent(new CustomEvent('nextjs-toploader:start'));
      };
    }

    if (originalDone) {
      (window as any).NProgress.done = function() {
        originalDone.call(this);
        // Emit custom event for loading complete
        window.dispatchEvent(new CustomEvent('nextjs-toploader:complete'));
      };
    }

    // Also listen for route changes via Next.js router events
    const handleRouteChangeStart = () => {
      window.dispatchEvent(new CustomEvent('nextjs-toploader:start'));
    };

    const handleRouteChangeComplete = () => {
      window.dispatchEvent(new CustomEvent('nextjs-toploader:complete'));
    };

    // Listen for Next.js router events
    window.addEventListener('beforeunload', handleRouteChangeStart);
    window.addEventListener('load', handleRouteChangeComplete);

    return () => {
      window.removeEventListener('beforeunload', handleRouteChangeStart);
      window.removeEventListener('load', handleRouteChangeComplete);
    };
  }, []);

  return (
    <NextTopLoader
      color="#FF2523"
      showSpinner={false}
      height={3}
      shadow="0 0 10px #FF2523,0 0 5px #FF2523"
      template='<div class="bar" role="bar"><div class="peg"></div></div> 
      <div class="spinner" role="spinner"><div class="spinner-icon"></div></div>'
      zIndex={1600}
      crawl={true}
      speed={200}
      easing="ease"
    />
  );
};

export default CustomTopLoader;
